import { Box, Button, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useUpdateView } from '~/api/views';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import { assert } from '~/lib/assert';
import { toDataUri } from '~/lib/file';
import type { ViewDetails__View as View } from './details.generated';

export interface EditViewDialogProps {
  close: () => void;
  open: boolean;
  view: View | undefined;
}

export const EditViewDialog = ({ close, open, view }: EditViewDialogProps) => {
  const { t } = useTranslation();
  const [updateView] = useUpdateView();

  const formik = useFormik({
    initialValues: {
      name: view?.name ?? '',
      thumbnailUri: view?.thumbnailUri,
    },
    onSubmit: async (values) => {
      assert(view != null, 'EditViewDialog#onSubmit: view is nullish');

      const { errors } = await updateView({
        variables: {
          viewId: view.id,
          patch: {
            name: values.name,
            // The seeded value is the current thumbnail's URL; re-sending it would replace the thumbnail.
            thumbnailUri:
              values.thumbnailUri === view.thumbnailUri ? undefined : values.thumbnailUri,
          },
        },
      });
      if (errors?.length) return;

      close();
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: object({
      name: string().required().trim(),
      thumbnailUri: string().nullable(),
    }),
  });

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const thumbnailUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('thumbnailUri', thumbnailUri, true);
    },
  });

  if (view == null) return null;

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      onClose={close}
      open={open}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>{t('pages.shows.detail.dialogs.edit_view.title')}</DialogTitle>

      <DialogContent>
        <DialogForm id="edit-view" onSubmit={formik.handleSubmit}>
          <label>
            <span className="label required">{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>

          <label>
            <span className="label">{t('common.labels.thumbnail')}</span>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {formik.values.thumbnailUri ? (
                <Box
                  alt={t('common.labels.current_thumbnail')}
                  component="img"
                  src={formik.values.thumbnailUri}
                  sx={{
                    borderRadius: 1,
                    width: 200,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    borderRadius: 1,
                    width: 200,
                    height: 112,
                    overflow: 'hidden',
                  }}
                >
                  <LayoutSkeleton layout={view.layout} />
                </Box>
              )}
              <input {...getInputProps()} />
              <Button onClick={selectFile} variant="outlined">
                {t('pages.shows.detail.dialogs.edit_view.upload_thumbnail_button')}
              </Button>
            </Box>
          </label>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          form="edit-view"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useEditViewDialog = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>();

  const close = useCallback(() => setOpen(false), []);
  const showDialog = useCallback((newView: View) => {
    setView(newView);
    setOpen(true);
  }, []);

  return useMemo(
    () => ({ show: showDialog, props: { close, open, view } }),
    [close, open, showDialog, view],
  );
};
